import { ref, nextTick, Ref, onBeforeUnmount } from '~/utils/nuxt3-migration'
import { useNamespacedStore } from '~/compositions/store'
import { PAGE_NS, PageState } from '~/store/modules/shared/page/state'
import { SET_MODAL_OPEN } from '~/store/modules/shared/page/mutation-types'

let onHashChangeCallback: Function | null = null

export function useModalToggle(
  closeModal: Function = () => {},
  afterModalClose: Function = () => {},
  noCloseOnEsc?: Ref<boolean>
) {
  const { dispatch, commit } = useNamespacedStore<PageState>(PAGE_NS)

  const forceGoBack = ref(false)
  const hash = ref('modal')
  const modalInSearch = ref(false)

  const afterModalCloseCallback = ref(null as any)
  const onModalCloseCallback = ref(null as any)

  const getHashStack = () => {
    return window.location.hash.split('#').filter(Boolean)
  }

  const modalOpen = async () => {
    dispatch('addBodyClass', 'tw-pr-0')
    const wbefore = document.body.clientWidth
    dispatch('addBodyClass', 'modal-open')

    if (wbefore < document.body.clientWidth) {
      // this means we had a scrollbar and its gone, so add margin
      const px = (document.body.clientWidth - wbefore).toString() + 'px'
      document.body.style.marginRight = px
    }

    const stack = getHashStack()
    let multiHash = false

    if (stack.includes(hash.value)) {
      // Clean existing identical hash before re-adding it
      stack.splice(stack.indexOf(hash.value), 1)
      const url = new URL(window.location.href)
      url.hash = '#' + stack.join('#')
      window.history.replaceState(
        { resetSearchState: true },
        document.title,
        url.href
      )
    }

    if (stack.length > 0) {
      multiHash = true
      window.history.replaceState(
        { multiHash: true },
        document.title,
        window.location.href
      )
    }

    if (modalInSearch.value) {
      window.history.replaceState(
        { isInSearchPage: false, resetSearchState: true, multiHash: true },
        '',
        window.location.href
      )
    }

    commit(SET_MODAL_OPEN, true)
    await nextTick()

    stack.push(hash.value)

    const newUrl = new URL(window.location.href)
    if (modalInSearch.value || multiHash) {
      newUrl.hash = window.location.hash + '#' + hash.value
      window.history.pushState(null, document.title, newUrl.href)
    } else {
      newUrl.hash = '#' + hash.value
      window.history.pushState(null, document.title, newUrl.href)
    }

    window.addEventListener('keyup', keyup)
    window.addEventListener('hashchange', hashChange, false)
    document.body.style.paddingRight = ''
  }

  const modalClose = (
    backPressed: boolean,
    onlyRemoveClasses: boolean = false
  ) => {
    if (!onlyRemoveClasses) {
      closeModal()
      if (typeof onModalCloseCallback.value === 'function') {
        onModalCloseCallback.value()
      }
    }

    if (!backPressed && !onlyRemoveClasses) {
      forceGoBack.value = true
      window.history.go(-1)
    }

    const stack = getHashStack()
    const isLastModal = stack.length === 0

    if (isLastModal) {
      removeModalClasses()
      // modal listeners will be removed on hashchange
    }
  }

  const hashChange = (event: HashChangeEvent) => {
    if (onHashChangeCallback && window.location.hash !== '') {
      onHashChangeCallback(event)
      return
    }

    const stack = getHashStack()

    if (!forceGoBack.value && !stack.includes(hash.value)) {
      modalClose(true)
    } else {
      forceGoBack.value = false
    }

    afterModalClose()
    if (typeof afterModalCloseCallback.value === 'function') {
      afterModalCloseCallback.value()
    }

    if (stack.length === 0) {
      removeModalClasses()
      removeModalListeners()
    }
  }

  const goToNextHash = (nextHash: string) => {
    const stack = getHashStack()
    stack.push(nextHash)
    const url = new URL(window.location.href)
    url.hash = '#' + stack.join('#')
    window.history.pushState(null, document.title, url.href)
  }

  const keyup = (event: KeyboardEvent) => {
    const stack = getHashStack()
    const topHash = stack[stack.length - 1]

    if (
      event.key === 'Escape' &&
      !noCloseOnEsc?.value &&
      topHash === hash.value
    ) {
      modalClose(false)
    }
  }

  const setAfterModalCloseCallback = (cBack: Function) => {
    afterModalCloseCallback.value = cBack
  }

  const setOnModalCloseCallback = (cBack: Function) => {
    onModalCloseCallback.value = cBack
  }

  const onModalHide = (evt: { trigger: string }) => {
    if (
      ['unmount', 'backdrop', 'esc', 'headerclose', 'ok', 'cancel'].includes(
        evt.trigger
      )
    ) {
      modalClose(evt.trigger === 'unmount')

      if (evt.trigger === 'unmount') {
        const stack = getHashStack()
        const isLastModal = stack.length === 1 && stack[0] === hash.value
        if (isLastModal || stack.length === 0) {
          removeModalClasses()
        }
      }
    }
  }

  const removeModalListeners = () => {
    window.removeEventListener('hashchange', hashChange)
    window.removeEventListener('keyup', keyup)

    onHashChangeCallback = null
  }

  const removeModalClasses = async () => {
    dispatch('removeBodyClass', 'modal-open')
    document.body.style.marginRight = ''
    commit(SET_MODAL_OPEN, false)
    await nextTick()
    dispatch('removeBodyClass', 'tw-pr-0')
  }

  const setHash = (hashValue: string) => {
    hash.value = hashValue || 'modal'
  }

  const setModalInSearch = (inSearch: boolean = false) => {
    modalInSearch.value = inSearch
  }

  const setOnHashChangeCallback = (cBack: Function) => {
    onHashChangeCallback = cBack
  }

  onBeforeUnmount(() => {
    onModalHide({ trigger: 'unmount' })
  })

  return {
    modalOpen,
    modalClose,
    setAfterModalCloseCallback,
    setOnModalCloseCallback,
    onModalHide,
    setHash,
    setModalInSearch,
    setOnHashChangeCallback,
    goToNextHash
  }
}
